import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  BookOpen,
  Gauge,
  GraduationCap,
  Heart,
  HelpCircle,
  Home,
  LayoutDashboard,
  LayoutGrid,
  Library,
  LifeBuoy,
  LogOut,
  Megaphone,
  Menu,
  Newspaper,
  Plus,
  Rocket,
  Settings,
  Share2,
  ShieldCheck,
  Sparkles,
  Store,
  UserCircle2,
  Users,
  UserCheck,
  X,
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import BrandWordmark from "@/components/BrandWordmark";
import OrgCoBrand from "@/components/OrgCoBrand";
import IfModuleVisible from "@/components/IfModuleVisible";

import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import ThemeToggle from "@/components/ThemeToggle";
import { useSidebarDrawer } from "@/components/SidebarDrawerContext";
import { SUPPORT_TICKETS_CHANGED_EVENT } from "@/lib/supportEvents";
import { useAuth } from "@/contexts/AuthContext";
import { useNiva } from "@/contexts/NivaContext";
import { api, assetUrl } from "@/lib/api";
import { isFreeBuyerUser } from "@/lib/access";
import { RESOURCE_PAGE_ROLES } from "@/lib/resources";
import { orgScopedPath } from "@/lib/orgDoor";
import { ADMIN_ROLES, isPlatformAdmin } from "@/lib/roles";
import { cn } from "@/lib/utils";

// ── Breadcrumbs (sidebar-shell roles) ────────────────────────────────
// Buyer / admin / BL advisor keep their navigation in the left sidebar, so the
// top bar shows a "Section / Page" breadcrumb instead of duplicate tab links.
// Longest matching prefix wins; unlisted routes fall back to a prettified last
// path segment under the role's name.
const BREADCRUMB_RULES: { prefix: string; section: string; page: string }[] = [
  { prefix: "/buyer/dashboard", section: "Discover", page: "Dashboard" },
  { prefix: "/buyer/home", section: "Discover", page: "Available Listings" },
  { prefix: "/buyer/deal-scoring", section: "Discover", page: "AI Deal Matching" },
  { prefix: "/buyer/favorites", section: "Discover", page: "Favorites" },
  { prefix: "/buyer/interests", section: "Discover", page: "My Interests" },
  { prefix: "/buyer/pipeline", section: "Discover", page: "Deal CRM" },
  { prefix: "/buyer/sba-calculator", section: "Tools", page: "SBA Calculator" },
  { prefix: "/buyer/valuation-calculator", section: "Tools", page: "Valuation Calculator" },
  { prefix: "/buyer/upcoming-modules", section: "New Modules", page: "Upcoming Modules" },
  { prefix: "/buyer/settings/profile", section: "Settings", page: "My Profile" },
  { prefix: "/buyer/settings/security", section: "Settings", page: "Password & Security" },
  { prefix: "/buyer/settings/notifications", section: "Settings", page: "Email Notifications" },
  { prefix: "/buyer/settings/subscription", section: "Settings", page: "Subscription Plan" },
  { prefix: "/buyer/settings/payment", section: "Settings", page: "Payment Method" },
  { prefix: "/buyer/settings/history", section: "Settings", page: "Payment History" },
  { prefix: "/buyer/settings/documents", section: "Settings", page: "Documents" },
  { prefix: "/buyer/settings", section: "Account", page: "Settings" },
  { prefix: "/buyer/support", section: "Account", page: "Support" },
  { prefix: "/resources/glossary", section: "Resource", page: "Glossary" },
  { prefix: "/resources/faq", section: "Resource", page: "FAQ" },
  { prefix: "/resources/news", section: "Resource", page: "Latest News & Articles" },
  { prefix: "/resources/press", section: "Resource", page: "Press & Media Coverage" },
  { prefix: "/resources/mastermind", section: "Resource", page: "Mastermind" },
  { prefix: "/admin/resource-content", section: "Admin", page: "Resource Content" },
  { prefix: "/buyer/profile", section: "Account", page: "My Profile" },
  { prefix: "/choose-plan", section: "Account", page: "Choose Plan" },
  { prefix: "/listings/", section: "Discover", page: "Listing Details" },
  { prefix: "/bl-advisor/settings/profile", section: "Settings", page: "My Profile" },
  { prefix: "/bl-advisor/settings/security", section: "Settings", page: "Password & Security" },
  { prefix: "/bl-advisor/settings/documents", section: "Settings", page: "Documents" },
  { prefix: "/bl-advisor/settings", section: "Account", page: "Settings" },
  { prefix: "/bl-advisor/upcoming-modules", section: "New Modules", page: "Upcoming Modules" },
  { prefix: "/bl-advisor/dashboard", section: "Advisor", page: "Dashboard" },
  { prefix: "/bl-advisor/home", section: "Advisor", page: "Marketplace" },
  { prefix: "/bl-advisor/referrals", section: "Advisor", page: "Referrals" },
  { prefix: "/bl-advisor/interests", section: "Advisor", page: "Interests" },
  { prefix: "/advisor/assignments", section: "Advisor", page: "My Assignments" },
  { prefix: "/admin/home", section: "Admin", page: "Dashboard" },
  { prefix: "/admin/all-listings", section: "Admin", page: "All Listings" },
  { prefix: "/admin/moderation", section: "Admin", page: "Moderation" },
  { prefix: "/admin/lead-pipeline", section: "Admin", page: "Coming Soon Deals" },
  { prefix: "/admin/interests", section: "Admin", page: "Interests" },
  { prefix: "/admin/users", section: "Admin", page: "Users" },
  { prefix: "/admin/announcements", section: "Admin", page: "Announcements" },
  { prefix: "/admin/referrals", section: "Admin", page: "Referrals" },
  { prefix: "/admin/assignments", section: "Admin", page: "Assignments" },
  { prefix: "/admin/settings/profile", section: "Settings", page: "My Profile" },
  { prefix: "/admin/settings/security", section: "Settings", page: "Password & Security" },
  { prefix: "/admin/settings/documents", section: "Settings", page: "Documents" },
  { prefix: "/admin/settings", section: "Account", page: "Settings" },
];

const prettifySegment = (segment: string) =>
  segment.split("-").map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(" ");

function breadcrumbFor(pathname: string, fallbackSection: string, orgSlug?: string | null): { section: string; page: string } {
  // Rules are written without an organization prefix, so strip it before
  // matching — otherwise every /<slug>/buyer/... path falls through to the
  // prettified-last-segment fallback.
  if (orgSlug && pathname.startsWith(`/${orgSlug}/`)) {
    pathname = pathname.slice(orgSlug.length + 1);
  }
  const match = BREADCRUMB_RULES
    .filter((r) => pathname === r.prefix || pathname.startsWith(r.prefix))
    .sort((a, b) => b.prefix.length - a.prefix.length)[0];
  if (match) return match;
  const segments = pathname.split("/").filter(Boolean);
  return { section: fallbackSection, page: prettifySegment(segments[segments.length - 1] ?? "Home") };
}

function BadgeCount({ count }: { count: number }) {
  if (count <= 0) return null;

  return (
    <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground">
      {count > 99 ? "99+" : count}
    </span>
  );
}

function displayRole(role: string) {
  if (role === "bl_advisor") return "BL Advisor";
  return role;
}

export default function Navigation({ showBrand = true, showSettings = true, showBorder = true, logoVariant = "auto", logoLockup = false, showThemeToggle: showThemeToggleProp, coBrand = null }: { showBrand?: boolean; showSettings?: boolean; showBorder?: boolean; logoVariant?: "auto" | "dark" | "light"; /** Use the live-text lockup instead of the baked-in wordmark image (landing page — the raster tagline is unreadable at nav size). */ logoLockup?: boolean; /** Force the theme toggle on or off. Pass false on surfaces that pin data-theme themselves (e.g. an org landing page), where a toggle would look broken. Defaults to "on everywhere except the landing page". */ showThemeToggle?: boolean; /** Organization the page belongs to: its logo is shown beside the platform mark and its slug points the Sign In / Register CTAs at that org's doors. Organization landing pages only, never the app-wide chrome. */ coBrand?: { logoUrl: string | null; name: string; slug: string } | null }) {
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  /* Inside AppShell the role sidebar is the real navigation, and below `lg` it
     renders as a drawer this header opens. Only pages with no sidebar (sellers,
     logged-out visitors) still need the inline menu below, which is a partial,
     hand-maintained copy of the sidebar and drifts from it. */
  const sidebarDrawer = useSidebarDrawer();
  // Whichever of the two the hamburger is currently driving.
  const menuOpen = sidebarDrawer ? sidebarDrawer.open : mobileOpen;
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isAdmin = hasRole(...ADMIN_ROLES);
  // Platform admin only — org admins must not reach platform-wide authoring.
  const isPlatformAdminUser = isPlatformAdmin(user);
  const isBuyer = hasRole("buyer");
  const isBlAdvisor = hasRole("bl_advisor");
  const isSeller = hasRole("seller");
  const canListBusiness = hasRole("seller", "bl_advisor", ...ADMIN_ROLES);
  const canSeeMyListings = hasRole("seller", "bl_advisor", ...ADMIN_ROLES);
  const canSeeInterests = hasRole(...ADMIN_ROLES, "bl_advisor", "seller");
  // Upcoming Modules is member-only — the buyer sidebar hides it from Basic
  // (free) buyers, so the top nav must too.
  const canSeeUpcomingModules = !isFreeBuyerUser(user);
  // Resource pages are role-gated by RESOURCE_PAGE_ROLES, which currently
  // excludes Seller — don't offer a link that would only bounce them to "/".
  // Adding Seller back to that list restores the entry points automatically.
  // The library is also member-only, so Basic (free) buyers don't get the link.
  const canSeeResources = hasRole(...RESOURCE_PAGE_ROLES) && !isFreeBuyerUser(user);

  // Role-based URL prefix. Admins of a non-default organization additionally
  // work under /<slug>/admin/... so the URL names the console they're in.
  const orgPath = (path: string) => orgScopedPath(path, user?.org_slug);
  // Every role's workspace lives under its organization when the user belongs
  // to one — /ecfa/buyer/..., /ecfa/bl-advisor/..., /ecfa/admin/... — so the
  // URL always names the organization you are working in.
  const roleBase = isAdmin ? "/admin" : isBlAdvisor ? "/bl-advisor" : isSeller ? "/seller" : isBuyer ? "/buyer" : "";
  const rolePrefix = roleBase ? orgPath(roleBase) : "";
  const homeUrl = `${rolePrefix}/home`;
  const listingsUrl = `${rolePrefix}/listings`;
  const newListingUrl = `${rolePrefix}/new-listing`;
  const interestsUrl = `${rolePrefix}/interests`;
  // There is no /admin roadmap page — admins (who may also hold buyer/seller)
  // use the buyer one, which their roles are allowed on.
  const upcomingModulesUrl = `${isAdmin ? orgPath("/buyer") : rolePrefix}/upcoming-modules`;
  const favoritesUrl = isAdmin ? "/favorites" : `${rolePrefix}/favorites`;
  // A buyer without an active subscription is gated to /choose-plan, and the
  // role-prefixed /buyer/support bounces them right back (see RoleRoute). Point
  // them at the subscription-agnostic /support route so support stays reachable
  // while they're on the choose-plan page.
  const supportUrl = isBuyer && !isAdmin && !user?.has_active_subscription ? "/support" : `${rolePrefix}/support`;
  const profileUrl = `${rolePrefix}/profile`;
  const dashboardUrl = orgPath("/bl-advisor/dashboard");
  const referralsUrl = orgPath("/bl-advisor/referrals");
  const assignmentsUrl = isAdmin ? `${rolePrefix}/assignments` : orgPath("/advisor/assignments");

  const [interestCount, setInterestCount] = useState(0);
  const [newListingCount, setNewListingCount] = useState(0);
  const [newUserCount, setNewUserCount] = useState(0);
  /* Tickets waiting on a reply from support. Admin-only: this button goes to
     the queue at /admin/support, and for everyone else it goes to their own
     tickets, where the number would be meaningless. */
  const [openTicketCount, setOpenTicketCount] = useState(0);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!user || !canSeeInterests) return;
    api
      .get<{ count: number }>("/interests/unread-count")
      .then((data) => setInterestCount(data.count))
      .catch(() => {});
  }, [user, canSeeInterests]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    api
      .get<{ count: number }>("/business-listings/new-count")
      .then((data) => setNewListingCount(data.count))
      .catch(() => {});
  }, [user, isAdmin]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    api
      .get<{ count: number }>("/users/new-count")
      .then((data) => setNewUserCount(data.count))
      .catch(() => {});
  }, [user, isAdmin]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    const load = () => api
      .get<{ count: number }>("/support/admin/awaiting-count")
      .then((data) => setOpenTicketCount(data.count))
      .catch(() => {});
    void load();
    // Answering a ticket on /admin/support clears its marker; recount so the
    // badge does not sit on the number it fetched when the page first loaded.
    window.addEventListener(SUPPORT_TICKETS_CHANGED_EVENT, load);
    return () => window.removeEventListener(SUPPORT_TICKETS_CHANGED_EVENT, load);
  }, [user, isAdmin]);

  const handleLogout = () => {
    const wasAdmin = isAdmin;
    const slug = user?.org_slug ?? null;
    logout();
    // Back to the door they came in through, not the main site's.
    navigate(orgScopedPath(wasAdmin ? "/admin/sign-in" : "/sign-in", slug));
  };

  // Sidebar-shell roles get the breadcrumb + global-search top bar; sellers and
  // logged-out visitors keep the classic tab links (they have no sidebar).
  const breadcrumbNav = !!user && (isAdmin || isBlAdvisor || isBuyer);
  const crumb = breadcrumbFor(
    location.pathname,
    isAdmin ? "Admin" : isBlAdvisor ? "Advisor" : "Buyer",
    user?.org_slug,
  );

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // The main page keeps the Dark · Gold signature look with no theme option
  // (auth pages don't render this nav at all — they never show the toggle).
  // Routes that pin data-theme themselves override this with the prop.
  const showThemeToggle = showThemeToggleProp ?? location.pathname !== "/";
  const { available: nivaAvailable, open: nivaOpen, openNiva } = useNiva();

  // On an organization's page the auth CTAs must lead to THAT organization's
  // doors — registering at the main site's /register would create a
  // BusinessLocating account instead of a member of this organization.
  const signInPath = coBrand ? `/${coBrand.slug}/sign-in` : "/sign-in";
  const registerPath = coBrand ? `/${coBrand.slug}/register` : "/register";

  const navLinkClass = (active: boolean, tone: "default" | "admin" = "default") =>
    cn(
      "inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors duration-200",
      tone === "admin"
        ? active
          ? "bg-rose-600 text-white"
          : "text-rose-700 hover:bg-rose-100"
        : active
          ? "bg-gold-gradient !text-primary-foreground"
          : "text-muted-foreground hover:bg-accent hover:text-foreground"
    );

  return (
    <header className={cn("sticky top-0 z-50 bg-background/95 backdrop-blur", showBorder && "border-b border-border/80")}>
      <div className="px-4 sm:px-6 lg:px-8">
        <nav className="relative flex h-16 items-center gap-3">
          <div className={cn("min-w-0 items-center gap-3", showBrand ? "flex" : "flex lg:hidden")}>
            <Link
              to={homeUrl}
              className="flex items-center"
            >
              {logoLockup ? (
                <BrandWordmark pinSize={38} />
              ) : user ? (
                /* Signed in, the header also carries Niva, Support, the theme
                   toggle, notifications and the menu button — five 40px targets.
                   The 181px-wide wordmark plus that cluster is ~457px, which
                   does not fit a 390px phone, so below `sm` the brand falls back
                   to the pin mark (the same one the collapsed sidebar uses). The
                   full wordmark is still in the drawer that menu button opens. */
                <>
                  <img src="/logo_mark.png" alt="BusinessLocating" className="h-10 w-10 object-contain sm:hidden" />
                  <BrandLogo variant={logoVariant} className="hidden h-10 w-auto sm:block" />
                </>
              ) : (
                /* Logged out the cluster is just the toggle and the menu, so the
                   wordmark fits at every width — the landing page keeps it. */
                <BrandLogo variant={logoVariant} className="h-10 w-auto" />
              )}
            </Link>
            {/* Co-brand lockup — the platform mark stays, the organization's
                logo sits beside it. Set on organization landing pages; the
                logo keeps its own brand colours, so it is never recoloured. */}
            {coBrand?.logoUrl && (
              <>
                <span className="h-6 w-px shrink-0 bg-border/80" aria-hidden="true" />
                <img
                  src={coBrand.logoUrl}
                  alt={coBrand.name}
                  className="h-12 w-auto max-w-[9rem] shrink-0 object-contain"
                />
              </>
            )}
          </div>

          {/* Breadcrumb bar — sidebar-shell roles (nav lives in the sidebar) */}
          {breadcrumbNav && (
            <div className="hidden min-w-0 flex-1 items-center gap-2 pl-2 text-sm lg:flex">
              <span className="whitespace-nowrap text-muted-foreground">{crumb.section}</span>
              <span className="text-muted-foreground/40">/</span>
              <span className="truncate font-semibold text-primary">{crumb.page}</span>
            </div>
          )}

          {!breadcrumbNav && (
          <div className="hidden lg:flex items-center gap-0.5 flex-1 pl-2">
                {user && !isAdmin && (
                  <Link to={homeUrl} className={navLinkClass(isActive(homeUrl))}>
                    <Home className="h-4 w-4" />
                    Home
                  </Link>
                )}
                {isAdmin && (
                  <Link to={homeUrl} className={navLinkClass(isActive(homeUrl))}>
                    <Home className="h-4 w-4" />
                    Home
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to={orgPath("/admin/all-listings")}
                    className={navLinkClass(isActive(orgPath("/admin/all-listings")))}
                  >
                    <LayoutGrid className="h-4 w-4" />
                    All Listings
                  </Link>
                )}
                {isBlAdvisor && (
                  <Link to={dashboardUrl} className={navLinkClass(isActive(dashboardUrl))}>
                    <Gauge className="h-4 w-4" />
                    Dashboard
                  </Link>
                )}
                {isBuyer && (
                  <Link to={interestsUrl} className={navLinkClass(isActive(interestsUrl))}>
                    <Heart className="h-4 w-4" />
                    My Interests
                  </Link>
                )}
                {user && !isAdmin && (
                  <Link to={favoritesUrl} className={navLinkClass(isActive(favoritesUrl))}>
                    <Heart className="h-4 w-4" />
                    Favorites
                  </Link>
                )}
                {canSeeMyListings && !isBlAdvisor && !isAdmin && (
                  <Link to={listingsUrl} className={navLinkClass(isActive(listingsUrl))}>
                    <LayoutDashboard className="h-4 w-4" />
                    My Listings
                  </Link>
                )}
                {canListBusiness && !isBlAdvisor && !isAdmin && (
                  <Link to={newListingUrl} className={navLinkClass(isActive(newListingUrl) || isActive("/listings/new"))}>
                    <Plus className="h-4 w-4" />
                    New Listing
                  </Link>
                )}
                {canSeeInterests && (
                  <Link
                    to={interestsUrl}
                    className={navLinkClass(isActive(interestsUrl))}
                    onClick={() => setInterestCount(0)}
                  >
                    <Heart className="h-4 w-4" />
                    Interests
                    <BadgeCount count={interestCount} />
                  </Link>
                )}
                {(isBuyer || isSeller) && canSeeUpcomingModules && (
                  <IfModuleVisible module="upcoming_modules">
                    <Link to={upcomingModulesUrl} className={navLinkClass(isActive(upcomingModulesUrl))}>
                      <Rocket className="h-4 w-4" />
                      Upcoming Modules
                    </Link>
                  </IfModuleVisible>
                )}
                {/* Resource lives in the left sidebar for buyer / admin / BL
                    advisor. This tab covers roles that have no sidebar — today
                    none qualify, but it lights up automatically when a
                    sidebar-less role is added to RESOURCE_PAGE_ROLES. */}
                {canSeeResources && (
                  <Link to="/resources/glossary" className={navLinkClass(isActive("/resources"))}>
                    <BookOpen className="h-4 w-4" />
                    Resource
                  </Link>
                )}
                {isAdmin && (
                  <IfModuleVisible module="moderation">
                    <Link
                      to={orgPath("/admin/moderation")}
                      className={navLinkClass(isActive(orgPath("/admin/moderation")))}
                      onClick={() => setNewListingCount(0)}
                    >
                      <Sparkles className="h-4 w-4" />
                      Moderation
                      <BadgeCount count={newListingCount} />
                    </Link>
                  </IfModuleVisible>
                )}
          </div>
          )}

          <div className="ml-auto hidden lg:flex items-center gap-2">
              {/* Organization logo. Sidebar-shell roles (admin, buyer, advisor)
                  hide the brand block on desktop — the sidebar carries the
                  platform mark — so the org's logo rides here in the top bar
                  instead, ahead of the controls. Skipped when an explicit
                  co-brand already shows it beside the brand block. */}
              {!coBrand && <OrgCoBrand className="h-14 max-w-[9rem]" divider="after" dividerClassName="h-8" />}
              {/* Ask Niva — the top-bar way into the assistant, for people who
                  never look at a floating bubble. Deliberately Support's twin
                  (same pill, same lucide-glyph-in-primary treatment) so the
                  cluster reads as one row of controls rather than one control
                  plus an advert; her portrait stays on the floating launcher and
                  in the drawer header, where it has room to be a face.
                  Opened through NivaContext — `available` there is the single
                  answer to "does she exist for this viewer" that the launcher
                  uses too, so a Basic buyer or a switched-off bot leaves no dead
                  button here. Highlights while the drawer is open, matching the
                  way Support highlights on its own page. */}
              {nivaAvailable && (
                <button
                  type="button"
                  onClick={openNiva}
                  title="Ask Niva — BL AI Deal Assistant"
                  aria-label="Ask Niva, the BL AI Deal Assistant"
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full border border-border/80 px-3 py-1.5 text-sm font-semibold text-foreground transition-colors duration-200 hover:bg-accent",
                    nivaOpen ? "bg-accent" : "bg-card"
                  )}
                >
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span className="hidden sm:inline">Ask Niva</span>
                </button>
              )}
              {/* Support — lives here rather than in the left sidebar so every
                  role reaches help from the same place (sidebar-shell roles show
                  a breadcrumb instead of tab links, so this cluster is the only
                  shared top-bar slot). Styled as the theme toggle's twin and
                  placed ahead of it. `!text-foreground` because the unlayered
                  `a { color: inherit }` in index.css otherwise beats the utility
                  on a <Link>. */}
              {user && (
                <Link
                  to={supportUrl}
                  title="Support"
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full border border-border/80 px-3 py-1.5 text-sm font-semibold !text-foreground transition-colors duration-200 hover:bg-accent",
                    isActive(supportUrl) ? "bg-accent" : "bg-card"
                  )}
                >
                  <LifeBuoy className="h-4 w-4 text-primary" />
                  <span className="hidden sm:inline">Support</span>
                  <BadgeCount count={openTicketCount} />
                </Link>
              )}
              {showThemeToggle && <ThemeToggle />}
              {user ? (
                <>
                  <NotificationBell />
                  <Link
                    to={profileUrl}
                    className="group flex items-center gap-2 rounded-lg border border-border/80 bg-card px-2 py-1.5 transition-colors duration-200 hover:bg-accent/50"
                  >
                    <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-xs font-bold text-primary">
                      {user.avatar_url ? (
                        <img src={assetUrl(user.avatar_url)!} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <>
                          {user.first_name?.[0]}
                          {user.last_name?.[0]}
                        </>
                      )}
                    </div>
                    <div className="hidden 2xl:block min-w-0">
                      <div className="text-sm font-semibold leading-tight text-foreground truncate">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="text-[0.64rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        {displayRole(user.roles?.[0] || "buyer")}
                      </div>
                    </div>
                  </Link>

                  {!isAdmin && showSettings ? (
                    <div className="relative" ref={userMenuRef}>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setUserMenuOpen((v) => !v)}
                        className="text-muted-foreground hover:text-foreground"
                        aria-label="Settings"
                      >
                        <Settings className="h-4 w-4" />
                      </Button>
                      {userMenuOpen && (
                        <div className="absolute right-0 top-full mt-2 w-48 rounded-lg border border-border/80 bg-card shadow-lg z-50 overflow-hidden">
                          {isBlAdvisor && (
                            <Link
                              to={referralsUrl}
                              className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground hover:bg-accent transition-colors"
                              onClick={() => setUserMenuOpen(false)}
                            >
                              <Share2 className="h-4 w-4 text-muted-foreground" />
                              Referrals
                            </Link>
                          )}
                          {isBlAdvisor && <div className="border-t border-border/60 my-0.5" />}
                          <button
                            className="flex w-full items-center gap-2 px-3 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors"
                            onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                          >
                            <LogOut className="h-4 w-4" />
                            Sign Out
                          </button>
                        </div>
                      )}
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  {/* Seller door. Platform-level page, so it is NOT org-scoped
                      the way signInPath/registerPath are — every visitor lands
                      on the same /seller inquiry form. */}
                  <Link to="/seller">
                    <Button variant="ghost" size="sm" className="gap-1.5">
                      <Store className="h-4 w-4" />
                      Sell Your Business
                    </Button>
                  </Link>
                  <Link to={signInPath}>
                    <Button variant="ghost" size="sm">
                      Sign In
                    </Button>
                  </Link>
                  <Link to={registerPath}>
                    <Button size="sm">
                      Register
                    </Button>
                  </Link>
                </>
              )}
          </div>

            {/* Mobile cluster — lg breakpoint (not xl) so a desktop at 125%
                browser zoom keeps the full header instead of the hamburger */}
            <div className="ml-auto flex items-center gap-2 lg:hidden">
              {/* Icon only on mobile — the label costs width the hamburger needs.
                  Shaped like the menu button beside it, not the desktop pill. */}
              {nivaAvailable && (
                <button
                  type="button"
                  onClick={openNiva}
                  aria-label="Ask Niva, the BL AI Deal Assistant"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border/80 bg-card text-foreground transition-colors duration-200 hover:bg-accent"
                >
                  <Sparkles className="h-5 w-5 text-primary" />
                </button>
              )}
              {/* Support. The desktop pill above is `hidden lg:flex`, and the
                  sidebar carries no Support entry, so on a phone help used to
                  live two taps deep inside the menu. Icon-only — the label
                  costs width this row does not have. */}
              {user && (
                <Link
                  to={supportUrl}
                  aria-label={openTicketCount > 0 ? `Support (${openTicketCount} awaiting a reply)` : "Support"}
                  className={cn(
                    "relative inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border/80 !text-foreground transition-colors duration-200 hover:bg-accent",
                    isActive(supportUrl) ? "bg-accent" : "bg-card"
                  )}
                >
                  <LifeBuoy className="h-5 w-5 text-primary" />
                  {/* A dot, not a number: there is no room for a count beside a
                      40px icon, and "there is something waiting" is the whole
                      message at this size. */}
                  {openTicketCount > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-background" />
                  )}
                </Link>
              )}
              {showThemeToggle && <ThemeToggle />}
              {user && <NotificationBell />}
              <button
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border/80 bg-card text-foreground transition-colors duration-200 hover:bg-accent"
                onClick={() => (sidebarDrawer ? sidebarDrawer.toggle() : setMobileOpen((value) => !value))}
                aria-label={menuOpen ? "Close navigation" : "Open navigation"}
                aria-expanded={menuOpen}
              >
                {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
        </nav>

          {mobileOpen && !sidebarDrawer && (
            <div className="border-t border-border/70 pb-4 pt-3 lg:hidden">
              <div className="grid gap-1.5">
                {user && !isAdmin && (
                  <Link to={homeUrl} className={navLinkClass(isActive(homeUrl))}>
                    <Home className="h-4 w-4" />
                    Home
                  </Link>
                )}
                {isAdmin && (
                  <Link to={homeUrl} className={navLinkClass(isActive(homeUrl))}>
                    <Home className="h-4 w-4" />
                    Home
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to={orgPath("/admin/all-listings")}
                    className={navLinkClass(isActive(orgPath("/admin/all-listings")))}
                  >
                    <LayoutGrid className="h-4 w-4" />
                    All Listings
                  </Link>
                )}
                {isBlAdvisor && (
                  <Link to={dashboardUrl} className={navLinkClass(isActive(dashboardUrl))}>
                    <Gauge className="h-4 w-4" />
                    Dashboard
                  </Link>
                )}
                {isBuyer && (
                  <Link to={interestsUrl} className={navLinkClass(isActive(interestsUrl))}>
                    <Heart className="h-4 w-4" />
                    My Interests
                  </Link>
                )}
                {user && !isAdmin && (
                  <Link to={favoritesUrl} className={navLinkClass(isActive(favoritesUrl))}>
                    <Heart className="h-4 w-4" />
                    Favorites
                  </Link>
                )}
                {canSeeMyListings && (
                  <Link to={listingsUrl} className={navLinkClass(isActive(listingsUrl))}>
                    <LayoutDashboard className="h-4 w-4" />
                    My Listings
                  </Link>
                )}
                {canListBusiness && (
                  <Link to={newListingUrl} className={navLinkClass(isActive(newListingUrl) || isActive("/listings/new"))}>
                    <Plus className="h-4 w-4" />
                    Create Listing
                  </Link>
                )}
                {canSeeInterests && (
                  <Link
                    to={interestsUrl}
                    className={navLinkClass(isActive(interestsUrl))}
                    onClick={() => setInterestCount(0)}
                  >
                    <Heart className="h-4 w-4" />
                    Interests
                    <BadgeCount count={interestCount} />
                  </Link>
                )}
                {isAdmin && (
                  <IfModuleVisible module="moderation">
                    <Link
                      to={orgPath("/admin/moderation")}
                      className={navLinkClass(isActive(orgPath("/admin/moderation")))}
                      onClick={() => setNewListingCount(0)}
                    >
                      <Sparkles className="h-4 w-4" />
                      Moderation
                      <BadgeCount count={newListingCount} />
                    </Link>
                  </IfModuleVisible>
                )}
                {isBlAdvisor && (
                  <Link to={referralsUrl} className={navLinkClass(isActive(referralsUrl))}>
                    <Share2 className="h-4 w-4" />
                    Referrals
                  </Link>
                )}
                {!isAdmin && user && (
                  <Link to={supportUrl} className={navLinkClass(isActive(supportUrl))}>
                    <LifeBuoy className="h-4 w-4" />
                    Support
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to="/admin/users"
                    className={navLinkClass(isActive("/admin/users"))}
                    onClick={() => setNewUserCount(0)}
                  >
                    <Users className="h-4 w-4" />
                    User Management
                    <BadgeCount count={newUserCount} />
                  </Link>
                )}
                {isAdmin && (
                  <Link to="/admin/support" className={navLinkClass(isActive("/admin/support"))}>
                    <LifeBuoy className="h-4 w-4" />
                    Support
                  </Link>
                )}
                {isAdmin && (
                  <Link to="/admin/announcements" className={navLinkClass(isActive("/admin/announcements"))}>
                    <Sparkles className="h-4 w-4" />
                    Announcements
                  </Link>
                )}
                {isAdmin && (
                  <Link to="/admin/lead-pipeline" className={navLinkClass(isActive("/admin/lead-pipeline"))}>
                    <Rocket className="h-4 w-4" />
                    Coming Soon
                  </Link>
                )}
                {(isBlAdvisor || isAdmin) && (
                  <Link to={assignmentsUrl} className={navLinkClass(isActive(assignmentsUrl))}>
                    <UserCheck className="h-4 w-4" />
                    My Assignments
                  </Link>
                )}
                {(isBuyer || isSeller || isBlAdvisor) && canSeeUpcomingModules && (
                  <IfModuleVisible module="upcoming_modules">
                    <Link to={upcomingModulesUrl} className={navLinkClass(isActive(upcomingModulesUrl))}>
                      <Rocket className="h-4 w-4" />
                      Upcoming Modules
                    </Link>
                  </IfModuleVisible>
                )}
                {/* Resource — the sidebar is desktop-only (hidden below lg), so
                    every permitted role reaches these pages through the mobile
                    menu. Gated by RESOURCE_PAGE_ROLES, which excludes Seller. */}
                {canSeeResources && (
                  <>
                    <Link to="/resources/glossary" className={navLinkClass(isActive("/resources/glossary"))}>
                      <BookOpen className="h-4 w-4" />
                      Glossary
                    </Link>
                    <Link to="/resources/faq" className={navLinkClass(isActive("/resources/faq"))}>
                      <HelpCircle className="h-4 w-4" />
                      FAQ
                    </Link>
                    <Link to="/resources/news" className={navLinkClass(isActive("/resources/news"))}>
                      <Newspaper className="h-4 w-4" />
                      Latest News &amp; Articles
                    </Link>
                    <Link to="/resources/press" className={navLinkClass(isActive("/resources/press"))}>
                      <Megaphone className="h-4 w-4" />
                      Press &amp; Media
                    </Link>
                    {/* Mastermind is staff-only — buyers and sellers never see it,
                        and neither do org admins: _STAFF_ROLES server-side is
                        bl_advisor + platform admin, so isAdmin (which includes
                        org_admin) would hand them a link to a 403. */}
                    {(isBlAdvisor || isPlatformAdminUser) && (
                      <Link to="/resources/mastermind" className={navLinkClass(isActive("/resources/mastermind"))}>
                        <GraduationCap className="h-4 w-4" />
                        Mastermind
                      </Link>
                    )}
                    {/* Authoring is platform-admin only — org admins are 403'd by
                        the route and the API, so don't advertise it to them. */}
                    {isPlatformAdminUser && (
                      <Link to="/admin/resource-content" className={navLinkClass(isActive("/admin/resource-content"))}>
                        <Library className="h-4 w-4" />
                        Manage Resources
                      </Link>
                    )}
                  </>
                )}
                {isAdmin && (
                  <Link to="/admin/referrals" className={navLinkClass(isActive("/admin/referrals"))}>
                    <Share2 className="h-4 w-4" />
                    BL Advisor Referrals
                  </Link>
                )}
              </div>

              <div className="mt-3 rounded-xl border border-border/70 bg-card p-3">
                {user ? (
                  <div className="space-y-3">
                    <Link to={profileUrl} className="flex items-center gap-3 rounded-lg px-1 py-1">
                      <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-sm font-bold text-primary">
                        {user.avatar_url ? (
                          <img src={assetUrl(user.avatar_url)!} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <>
                            {user.first_name?.[0]}
                            {user.last_name?.[0]}
                          </>
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-[0.68rem] uppercase tracking-[0.14em] text-muted-foreground">
                          {displayRole(user.roles?.[0] || "buyer")}
                        </div>
                      </div>
                    </Link>
                    <Button variant="outline" className="w-full justify-center" onClick={handleLogout}>
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Link to="/seller" className="block">
                      <Button variant="ghost" className="w-full justify-center">
                        <Store className="h-4 w-4" />
                        Sell Your Business
                      </Button>
                    </Link>
                    <Link to={signInPath} className="block">
                      <Button variant="outline" className="w-full justify-center">
                        <UserCircle2 className="h-4 w-4" />
                        Sign In
                      </Button>
                    </Link>
                    <Link to={registerPath} className="block">
                      <Button className="w-full justify-center">
                        <ShieldCheck className="h-4 w-4" />
                        Join Marketplace
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}
      </div>
    </header>
  );
}